import { useCallback, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";

// Reusable components
import { TableRow, TableCell } from "@/components/ui/table";

// Utils and helpers
import { searchFilterShifts } from "../utils/searchFilterShifts";
import { deriveShiftStatus, formatShiftDuration, shiftCashVariance } from "../utils/shiftHelpers";
import { StatusBadge } from "@/utils/resolveStatusStyle";
import { SHIFT_STATUS } from "@/utils/statusStyles";
import { formatDateTime } from "@/utils/formatDateTime";
import { formatCurrency } from "@/utils/formatCurrency";
import { fetchShifts } from "@/services/peopleApi";
import { useBusiness } from "@hooks/useBusiness";

export default function useShiftsTable() {
  const { businessId, users } = useBusiness();

  const {
    data: shifts = [],
    isLoading,
    isError,
    refetch,
  } = useQuery({
    // One Firestore doc per user holds an array of sessions; fetchShifts
    // flattens those into one row per shift.
    queryKey: ["shifts", businessId, Object.keys(users).length],
    queryFn: () => fetchShifts(businessId, users),
    enabled: Boolean(businessId),
    staleTime: 1000 * 60 * 5,
  });

  const [searchParams, setSearchParams] = useSearchParams();
  const searchTerm = searchParams.get("search") || "";
  const statusFilter = searchParams.get("status") || "All";
  const locationFilter = searchParams.get("location") || "All";

  const updateParam = useCallback(
    (key, val, emptyValue = "All") => {
      const params = new URLSearchParams(searchParams);
      if (val && val !== emptyValue) params.set(key, val);
      else params.delete(key);
      setSearchParams(params);
    },
    [searchParams, setSearchParams]
  );

  const updateSearchTerm = useCallback((val) => updateParam("search", val, ""), [updateParam]);
  const updateStatusFilter = useCallback((val) => updateParam("status", val), [updateParam]);
  const updateLocationFilter = useCallback((val) => updateParam("location", val), [updateParam]);

  const filteredShifts = useMemo(
    () => searchFilterShifts(shifts, searchTerm, statusFilter, locationFilter),
    [shifts, searchTerm, statusFilter, locationFilter]
  );

  // Distinct locations for the filter dropdown
  const locations = useMemo(
    () => [...new Set(shifts.map((s) => s.location).filter(Boolean))],
    [shifts]
  );

  const shiftColumns = useMemo(
    () => [
      {
        accessorKey: "shiftNumber",
        header: "Shift No.",
        cell: ({ row }) => row.original.shiftNumber || "-",
      },
      {
        accessorKey: "userName",
        header: "Cashier",
        cell: ({ row }) => row.original.userName || "-",
      },
      {
        accessorKey: "location",
        header: "Location",
        cell: ({ row }) => row.original.location || "-",
      },
      {
        accessorKey: "counter",
        header: "Counter",
        cell: ({ row }) => row.original.counter || "-",
      },
      {
        accessorKey: "startTime",
        header: "Started",
        cell: ({ row }) => formatDateTime(row.original.startTime),
      },
      {
        accessorKey: "endTime",
        header: "Ended",
        cell: ({ row }) =>
          row.original.endTime ? formatDateTime(row.original.endTime) : (
            <span className="text-gray-400 italic">In progress</span>
          ),
      },
      {
        id: "duration",
        header: "Duration",
        cell: ({ row }) =>
          formatShiftDuration(row.original.startTime, row.original.endTime),
      },
      {
        id: "variance",
        header: "Cash Variance",
        cell: ({ row }) => {
          const variance = shiftCashVariance(row.original);
          if (variance === null) return <span className="text-gray-400 italic">—</span>;
          const color =
            variance === 0 ? "text-gray-600" : variance > 0 ? "text-green-600" : "text-red-600";
          const sign = variance > 0 ? "+" : "";
          return (
            <span className={`font-medium ${color}`}>
              {sign}
              {formatCurrency(variance)}
            </span>
          );
        },
      },
      {
        id: "status",
        header: "Status",
        cell: ({ row }) => (
          <StatusBadge map={SHIFT_STATUS} status={deriveShiftStatus(row.original)} />
        ),
      },
    ],
    []
  );

  // View-only: rows are not interactive.
  const renderShiftRow = useCallback((row) => {
    return (
      <TableRow key={row.id} className="hover:bg-[#f6f6ff] transition-colors">
        {row.getVisibleCells().map((cell) => (
          <TableCell key={cell.id} className="px-4 py-3 text-[13px] font-medium">
            {cell.getContext().column.columnDef.cell?.(cell.getContext()) ?? cell.renderCell()}
          </TableCell>
        ))}
      </TableRow>
    );
  }, []);

  return {
    allShifts: shifts,
    shifts: filteredShifts,
    locations,
    searchTerm,
    updateSearchTerm,
    statusFilter,
    updateStatusFilter,
    locationFilter,
    updateLocationFilter,
    refetch,
    shiftColumns,
    renderShiftRow,
    isLoading,
    isError,
  };
}
